<script setup lang="ts">
import FillLockIcon from '@/components/common/icons/FillLockIcon.vue'
import { formatEventTimes } from '@/components/events/helper/EventsHelper'
import { isMobile } from '@/util/helper'
import {
  AlarmClockIcon,
  CalendarDateIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UISkeleton, UITextMdMedium, UITextXsMedium } from '@gohighlevel/ghl-ui'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

interface Position {
  left: number
  top: number
}

interface ParentRect {
  left: number
  right: number
  top: number
  height: number
  width: number
  parentElementRect: DOMRect
}

interface TooltipState {
  type: 'loading' | 'error' | 'success' | null
  message?: string
  eventName?: string
  eventData?: any
  formattedTime?: any
}
const show = ref(false)
const position = ref<Position>({ left: 0, top: 0 })
const parentRect = ref<ParentRect | null>(null)
const tooltipState = ref<TooltipState>({ type: null })
const { t } = useI18n()

function handleShowLoadingTooltip(event: CustomEvent) {
  position.value.left = event?.detail?.parent?.left
  position.value.top = event?.detail?.parent?.top
  parentRect.value = event?.detail?.parent
  tooltipState.value = {
    type: 'loading',
    eventName: event?.detail?.eventName,
  }
  show.value = true
}

function handleShowErrorTooltip(event: CustomEvent) {
  position.value.left = event?.detail?.parent?.left
  position.value.top = event?.detail?.parent?.top
  parentRect.value = event?.detail?.parent
  tooltipState.value = {
    type: 'error',
    eventName: event?.detail?.eventName,
    message:
      event?.detail?.errorMessage ||
      t('events.errorLoadingEvent', 'Failed to load event'),
  }
}

function handleHideTooltip() {
  if (show.value) {
    show.value = false
    tooltipState.value = { type: null }
  }
}

function handleShowSuccessTooltip(event: CustomEvent) {
  position.value.left = event?.detail?.parent?.left
  position.value.top = event?.detail?.parent?.top
  parentRect.value = event?.detail?.parent
  tooltipState.value = {
    type: 'success',
    eventData: event?.detail?.eventData,
    formattedTime: formatEventTimes(
      event?.detail?.eventData?.startTime,
      event?.detail?.eventData?.endTime
    ),
  }
}

onMounted(() => {
  const tooltipElement = document.getElementById('event-tooltip')
  if (tooltipElement) {
    tooltipElement.addEventListener(
      'show-event-loading-tooltip',
      handleShowLoadingTooltip as EventListener
    )
    tooltipElement.addEventListener(
      'show-event-error-tooltip',
      handleShowErrorTooltip as EventListener
    )
    tooltipElement.addEventListener(
      'show-event-success-tooltip',
      handleShowSuccessTooltip as EventListener
    )
    tooltipElement.addEventListener('close-event-tooltip', handleHideTooltip)
  }
  // Add scroll event listener to hide tooltip when scrolling
  window.addEventListener('scroll', handleHideTooltip)
  window.addEventListener('wheel', handleHideTooltip)
})

onUnmounted(() => {
  const tooltipElement = document.getElementById('event-tooltip')
  tooltipElement?.removeEventListener(
    'show-event-loading-tooltip',
    handleShowLoadingTooltip as EventListener
  )
  tooltipElement?.removeEventListener(
    'show-event-error-tooltip',
    handleShowErrorTooltip as EventListener
  )
  tooltipElement?.removeEventListener('close-event-tooltip', handleHideTooltip)
  window.removeEventListener('scroll', handleHideTooltip)
})

watch(tooltipState, () => {
  if (!show.value || !parentRect.value) return

  nextTick(() => {
    const tooltipElement = document.getElementById('event-tooltip')
    if (!tooltipElement) return

    const tooltipHeight = tooltipElement.getBoundingClientRect().height
    const spaceAbove = parentRect.value!.top
    const toolTipOffset = 8 // Reduced from 30px for better positioning

    // Check if there's enough space above the target element
    if (spaceAbove > tooltipHeight + toolTipOffset) {
      // Position above the target
      position.value.top = parentRect.value!.top - tooltipHeight - toolTipOffset
    } else {
      // Position below the target
      position.value.top =
        parentRect.value!.top + parentRect.value!.height + toolTipOffset
    }
  })
})
</script>

<template>
  <teleport to="body">
    <div
      v-show="show && !isMobile()"
      id="event-tooltip"
      role="tooltip"
      class="fixed min-w-[120px] overflow-hidden rounded-lg border border-solid border-communities-border bg-communities-background p-3 text-center text-communities-font-primary shadow-lg"
      :style="{
        top: position.top + 'px',
        left: position.left + 'px',
        zIndex: 3000,
        maxWidth: tooltipState.type === 'error' ? '250px' : '300px',
      }"
    >
      <!-- Loading State -->
      <div v-if="tooltipState.type === 'loading'" class="flex flex-col gap-0">
        <!-- Event title skeleton -->
        <UISkeleton height="20px" width="70%" :sharp="false" class="mb-2" />

        <div class="flex flex-row gap-2">
          <!-- Time details skeleton -->
          <div class="flex flex-row items-center gap-1">
            <AlarmClockIcon class="h-4 w-4" />
            <UISkeleton height="16px" width="60px" :sharp="false" />
          </div>
          <!-- Date details skeleton -->
          <div class="flex flex-row items-center gap-1">
            <CalendarDateIcon class="h-4 w-4" />
            <UISkeleton height="16px" width="60px" :sharp="false" />
          </div>
        </div>
      </div>

      <!-- Success State -->
      <div
        v-else-if="tooltipState.type === 'success'"
        class="flex flex-col gap-0"
      >
        <UITextMdMedium id="event-title" class="line-clamp-1 text-left">
          {{ tooltipState.eventData?.title }}
        </UITextMdMedium>
        <div class="flex flex-row gap-2">
          <div class="flex flex-row items-center gap-1">
            <AlarmClockIcon class="h-4 w-4" />
            <UITextXsMedium id="event-time-details">{{
              `${tooltipState.formattedTime.fromTime} - ${tooltipState.formattedTime?.toTime}`
            }}</UITextXsMedium>
          </div>
          <div class="flex flex-row items-center gap-1">
            <CalendarDateIcon class="h-4 w-4" />
            <UITextXsMedium id="event-date-details">{{
              tooltipState.formattedTime?.date
            }}</UITextXsMedium>
          </div>
        </div>
      </div>
      <!-- Error State -->
      <div
        v-else-if="tooltipState.type === 'error'"
        class="flex items-center text-xs font-semibold text-communities-font-primary"
      >
        <FillLockIcon
          class="h-5 w-5 text-communities-font-primary"
          :fill="'var(--fontPrimary)'"
        />
        {{
          tooltipState.message === 'not-accessible'
            ? t('events.eventRestricted')
            : t('events.eventDeleted')
        }}
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.community-bottom-sheet {
  z-index: 200 !important;
}
.swipe-modal::backdrop {
  backdrop-filter: blur(1px) !important;
}
</style>
